/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/reasonCodeOperationModal/reasonCodeOperationModal"/>
import { Button, ButtonGroup, DialogPart, EmptyState, FlexLayout, FlexSpacer, ImageMessagessCleaninbox, InstantSearchBox, LocalizationContext, ModalDialogShell, StringMap, Text } from '@medius/ui-controls';
import * as React from 'react';
import ReasonCodesGrid from 'Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/shared/reasonCodesGrid';
import { IInvoiceReasonCodeData, IReasonCode } from '../rejectionManagementWizard/interfaces';
import { translate } from 'Core/Medius.Core.Web/Scripts/lib/globalization';
import { getCultureCode } from 'Core/Medius.Core.Web/Scripts/Medius/lib/utils/user';
import { mapFromNorthstarToApa } from 'Core/Medius.Core.Web/Scripts/lib/northstarLocalization';
import { ReasonCodeOperationType } from '../shared/interfaces';

interface ReasonCodeOperationModalProps {
    title: string;
    submitLabel: string;
    searchLabel: string;
    reasonCodeData: IInvoiceReasonCodeData;
    onSubmit: (selected: IReasonCode[]) => Promise<void>;
    onClose: (confirmed: boolean) => void;
    preselectedReasons?: IReasonCode[];
    reasonCodeOperationType?: ReasonCodeOperationType;
}

export const translateKeyBase = "#PurchaseToPay/reasonCodeOperationModal";

export const ReasonCodeOperationModal = ({
    title,
    submitLabel,
    searchLabel,
    reasonCodeData,
    onSubmit,
    onClose,
    preselectedReasons,
    reasonCodeOperationType
}: ReasonCodeOperationModalProps) => {
    const [selection, setSelection] = React.useState<IReasonCode[]>(preselectedReasons || []);
    const [searchTerm, setSearchTerm] = React.useState("");
    const { searching: { changeSearchTerm } } = reasonCodeData;

    const isSelectionMandatory = selection.length === 0;
    const noReasonCodesAvailable = searchTerm === "" &&
        reasonCodeData.invoiceReasonCodes.length === 0 &&
        !reasonCodeData.isLoading;

    const handleSubmitButton = async () => {
        await onSubmit(selection);
    };

    // after release of version 5 of Northstar should be changed to use renderContainerRef prop in ModalDialogShell API https://medius.atlassian.net/browse/NDS-1704 
    React.useEffect(() => {
        const modal = document.querySelector('[data-testid="reason-code-operation-modal"]');
        const overlay = modal?.parentElement;

        if (overlay) {
            (overlay as HTMLElement).style.zIndex = "1111";
        }
    }, []);

    return (
        <LocalizationContext.Provider
            value={{
                translate: (key: string, _, defaultValue?: string, args?: StringMap) =>
                    mapFromNorthstarToApa(key, defaultValue, args),
                getLocale: () => getCultureCode()
            }}
        >
            <ModalDialogShell
                isOpen
                hasHeader
                height={'vlarge'}
                title={title}
                data-testid="reason-code-operation-modal"
                onRequestClose={() => onClose(false)} >
                {dialogProps => <>
                        <DialogPart.Title {...dialogProps.headerProps} />
                        <DialogPart.Content width="vlarge">
                            <div className='reason-code-operation-modal'>
                                {noReasonCodesAvailable ? 
                                    <FlexLayout verticalAlignment='center' horizontalAlignment='center' height='fill' width='fill'>
                                        <EmptyState
                                            contentAlign="center"
                                            title={translate(`${translateKeyBase}/noReasonCodesAvailableTitle`)}
                                            explanation={translate(`${translateKeyBase}/noReasonCodesAvailableExplanation`)}
                                            image={<ImageMessagessCleaninbox />}
                                            size='large'
                                        />
                                    </FlexLayout>
                                : (
                                    <div>
                                        <div>
                                            {searchLabel && 
                                                <Text variant="heading3" data-testid="reason-code-operation-modal-search-header">
                                                    {searchLabel}
                                                </Text>
                                            }
                                            <InstantSearchBox
                                                data-testid="reason-code-operation-modal-search-box"
                                                labelId="reason-code-operation-modal-search-box"
                                                width="fill"
                                                spacerMode="off"
                                                executeSearch={(term) => {
                                                    setSearchTerm(term);
                                                    changeSearchTerm(term);
                                                }}
                                            />
                                        </div>
                                        <div className="reason-code-operation-modal__content">
                                            <ReasonCodesGrid
                                                selection={selection}
                                                setSelection={setSelection}
                                                invoiceReasonCodeData={reasonCodeData}
                                                reasonCodeOperationType={reasonCodeOperationType}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </DialogPart.Content>
                        <DialogPart.Footer>
                            <FlexLayout>
                                <FlexSpacer />
                                <ButtonGroup>
                                    <Button
                                        data-testid="reason-code-operation-modal-cancel-button"
                                        label={translate("#Core/cancel")}
                                        variant="secondary"
                                        onClick={() => onClose(false)}
                                    />
                                    <Button
                                        data-testid="reason-code-operation-modal-submit-button"
                                        label={submitLabel}
                                        disabled={isSelectionMandatory}
                                        onClick={handleSubmitButton}
                                    />
                                </ButtonGroup>
                            </FlexLayout>
                        </DialogPart.Footer>
                    </>
                }
            </ModalDialogShell>
        </LocalizationContext.Provider>
    );
};